/**
 * CrowdfundPanel - Component for model crowdfunding donations
 * 
 * Shows a golden donation panel for models that are in crowdfund mode
 * (crowdfund_enabled = true, no commands available).
 */

import { useState, useEffect, useRef, useMemo } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { useI18n } from '../../bootstrap/i18n';
import { trackEvent } from '../../analytics';
import './CrowdfundPanel.css';

interface CrowdfundData {
  enabled: boolean;
  goal: number;
  current: number;
  donationAmount: number;
  maxPerUser: number;
  percent: number;
  supportersCount: number;
  donationsCount: number;
  goalReached: boolean;
}

interface Props {
  modelId: number;
  modelName: string;
  onNeedTopup: () => void;
  compact?: boolean; // Embedded in commands area, collapsible
}

export function CrowdfundPanel({ modelId, modelName, onNeedTopup, compact = false }: Props) {
  const services = useMemo(() => bootOnce(), []);
  const { t } = useI18n(services.i18n);
  
  const [data, setData] = useState<CrowdfundData | null>(null);
  const [loading, setLoading] = useState(true);
  const [donating, setDonating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false); // For compact mode
  const viewTrackedRef = useRef<number | null>(null); // modelId we already tracked view for
  
  // Fetch crowdfund status on mount
  useEffect(() => {
    loadCrowdfundData();
  }, [modelId]);
  
  // Track crowdfund_view when data is loaded
  useEffect(() => {
    if (!data?.enabled || loading) return;
    if (viewTrackedRef.current === modelId) return;
    viewTrackedRef.current = modelId;
    
    try {
      trackEvent('crowdfund_view' as any, {
        modelId,
        modelName,
        origin: 'chat',
        metadata: {
          goal: data.goal,
          current: data.current,
          percent: data.percent,
        },
      });
    } catch {}
  }, [data, loading, modelId, modelName]);
  
  async function loadCrowdfundData() {
    try {
      setLoading(true);
      setError(null);
      
      const apiBase = services.config.config.apiBaseUrl || '';
      const response = await fetch(`${apiBase}/api/model/${modelId}/crowdfund`);
      
      if (!response.ok) {
        throw new Error('Failed to load crowdfund data');
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('[CrowdfundPanel] Failed to load data:', err);
      setError(t('crowdfund.load_error', 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }
  
  async function handleDonate() {
    if (donating || !data) return;
    
    setDonating(true);
    setError(null);
    
    try {
      const apiBase = services.config.config.apiBaseUrl || '';
      const hasJwt = await services.supabase.waitForJwt({ timeoutMs: 5000 });
      const token = hasJwt ? services.supabase.getAccessToken() : null;
      
      if (!token) {
        setError(t('crowdfund.auth_error', 'Please sign in to donate'));
        setDonating(false);
        return;
      }
      
      const response = await fetch(`${apiBase}/api/model/${modelId}/crowdfund/donate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      
      const result = await response.json();
      
      if (response.ok && result.ok) {
        // Update local state with new values
        setData(prev => prev ? {
          ...prev,
          current: result.current,
          percent: result.percent,
          goalReached: result.goalReached || false,
          donationsCount: (prev.donationsCount || 0) + 1, // Optimistic increment
        } : null);
        
        // Track successful donation
        try {
          trackEvent('crowdfund_donate' as any, {
            modelId,
            modelName,
            origin: 'chat',
            metadata: {
              amount: data.donationAmount,
              newCurrent: result.current,
              percent: result.percent,
              goalReached: result.goalReached,
            },
          });
        } catch {}
        
        // Refresh credits balance in UI
        try { window.dispatchEvent(new Event('aicams-credits-refresh')); } catch {}
        
        // Show success animation
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      } else if (result.error === 'insufficient_balance') {
        // Track failed donation
        try {
          trackEvent('crowdfund_donate_failed' as any, {
            modelId,
            modelName,
            origin: 'chat',
            metadata: { reason: 'insufficient_balance' },
          });
        } catch {}
        // Not enough credits - open topup
        onNeedTopup();
      } else if (result.error === 'user_limit_reached') {
        // Track failed donation
        try {
          trackEvent('crowdfund_donate_failed' as any, {
            modelId,
            modelName,
            origin: 'chat',
            metadata: { reason: 'user_limit_reached' },
          });
        } catch {}
        setError(t('crowdfund.limit_reached', 'You reached your donation limit for this model'));
      } else if (result.error === 'goal_already_reached') {
        setError(t('crowdfund.goal_reached_already', 'Goal already reached!'));
        // Refresh data to show updated state
        loadCrowdfundData();
      } else {
        setError(result.error || t('crowdfund.error', 'Something went wrong'));
      }
    } catch (err) {
      console.error('[CrowdfundPanel] Donate error:', err);
      setError(t('crowdfund.error', 'Something went wrong'));
    } finally {
      setDonating(false);
    }
  }
  
  if (loading) {
    return (
      <div class={`crowdfundPanel crowdfundLoading ${compact ? 'compact' : ''}`}>
        <div class="crowdfundSpinner" />
      </div>
    );
  }
  
  if (!data?.enabled) {
    return null;
  }
  
  const percent = Math.min(100, data.percent || 0);
  
  // Compact mode: collapsible panel embedded in commands area
  if (compact) {
    return (
      <div class={`crowdfundPanel compact ${expanded ? 'expanded' : 'collapsed'} ${showSuccess ? 'success' : ''}`}>
        {/* Compact Header Row - always visible */}
        <div class="crowdfundCompactHeader" onClick={() => setExpanded(!expanded)}>
          <div class="compactLeft">
            <span class="crowdfundEmoji">🌟</span>
            <span class="compactTitle">{t('crowdfund.cta_short', 'Help me appear!')}</span>
            <span class="compactProgress">{percent}%</span>
          </div>
          <div class="compactRight">
            {!data.goalReached && (
              <button 
                class="crowdfundButton compactButton"
                onClick={(e) => { e.stopPropagation(); handleDonate(); }}
                disabled={donating}
              >
                {donating ? (
                  <span class="buttonSpinner" />
                ) : (
                  <>
                    <span class="buttonIcon">💫</span>
                    <span class="buttonText">
                      {t('crowdfund.donate_btn_short', '{amount} ⭐').replace('{amount}', String(data.donationAmount || 10))}
                    </span>
                  </>
                )}
              </button>
            )}
            <span class={`expandIcon ${expanded ? 'up' : 'down'}`}>▼</span>
          </div>
        </div>
        
        {/* Expanded Details */}
        {expanded && (
          <div class="crowdfundExpandedContent">
            {/* Description */}
            <p class="crowdfundDesc">
              {t('crowdfund.desc', 'Donate Stars to unlock my full scenario! Your support brings me to life faster.')}
            </p>
            
            {/* Progress Bar */}
            <div class="crowdfundProgress">
              <div class="progressBarContainer">
                <div 
                  class="progressBarFill" 
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div class="progressLabels">
                <span class="progressCurrent">
                  {(data.current || 0).toLocaleString()} / {(data.goal || 0).toLocaleString()} ⭐
                </span>
                <span class="progressPercent">{percent}%</span>
              </div>
            </div>
            
            {/* Goal Reached */}
            {data.goalReached && (
              <div class="crowdfundGoalReached">
                🎉 {t('crowdfund.goal_reached', 'Goal reached! Coming very soon...')}
              </div>
            )}
            
            {/* Error Message */}
            {error && (
              <div class="crowdfundError">{error}</div>
            )}
            
            {/* Donations Count */}
            <p class="crowdfundSupporters">
              💝 {t('crowdfund.donations_count', '{count} donations').replace('{count}', String(data.donationsCount || 0))}
            </p>
          </div>
        )}
        
        {/* Success Animation Overlay */}
        {showSuccess && (
          <div class="crowdfundSuccessOverlay">
            <span class="successEmoji">✨</span>
            <span class="successText">{t('crowdfund.thanks', 'Thank you!')}</span>
          </div>
        )}
      </div>
    );
  }
  
  // Full mode: original layout
  return (
    <div class={`crowdfundPanel ${showSuccess ? 'success' : ''}`}>
      {/* Header */}
      <div class="crowdfundHeader">
        <span class="crowdfundEmoji">🌟</span>
        <h3 class="crowdfundTitle">
          {t('crowdfund.cta_title', 'Help me appear!')}
        </h3>
      </div>
      
      {/* Description */}
      <p class="crowdfundDesc">
        {t('crowdfund.desc', 'Donate Stars to unlock my full scenario! Your support brings me to life faster.').replace('{name}', modelName)}
      </p>
      
      {/* Progress Bar */}
      <div class="crowdfundProgress">
        <div class="progressBarContainer">
          <div 
            class="progressBarFill" 
            style={{ width: `${percent}%` }}
          />
        </div>
        <div class="progressLabels">
          <span class="progressCurrent">
            {(data.current || 0).toLocaleString()} / {(data.goal || 0).toLocaleString()} ⭐
          </span>
          <span class="progressPercent">{percent}%</span>
        </div>
      </div>
      
      {/* Button or Goal Reached */}
      {data.goalReached ? (
        <div class="crowdfundGoalReached">
          🎉 {t('crowdfund.goal_reached', 'Goal reached! Coming very soon...')}
        </div>
      ) : (
        <button 
          class="crowdfundButton"
          onClick={handleDonate}
          disabled={donating}
        >
          {donating ? (
            <span class="buttonSpinner" />
          ) : (
            <>
              <span class="buttonIcon">💫</span>
              <span class="buttonText">
                {t('crowdfund.donate_btn', 'Donate {amount} ⭐').replace('{amount}', String(data.donationAmount || 10))}
              </span>
            </>
          )}
        </button>
      )}
      
      {/* Error Message */}
      {error && (
        <div class="crowdfundError">{error}</div>
      )}
      
      {/* Donations Count */}
      <p class="crowdfundSupporters">
        💝 {t('crowdfund.donations_count', '{count} donations').replace('{count}', String(data.donationsCount || 0))}
      </p>
      
      {/* Success Animation Overlay */}
      {showSuccess && (
        <div class="crowdfundSuccessOverlay">
          <span class="successEmoji">✨</span>
          <span class="successText">{t('crowdfund.thanks', 'Thank you!')}</span>
        </div>
      )}
    </div>
  );
}
